/**
 * Vuex 持久化插件
 * 将指定状态路径持久化到 localStorage，实现状态管理与持久化逻辑的解耦
 */

const STORAGE_KEY_PREFIX = 'app_'

/**
 * 从 localStorage 恢复状态
 * @param {Object} state - Vuex state 对象
 * @param {Array<string>} paths - 需要持久化的状态路径
 */
function restoreState(state, paths) {
  paths.forEach(path => {
    const key = STORAGE_KEY_PREFIX + path
    try {
      const stored = localStorage.getItem(key)
      if (stored !== null) {
        // 尝试解析 JSON，如果不是 JSON 字符串则直接使用
        try {
          const parsed = JSON.parse(stored)
          // 特殊处理 token，确保它是字符串而不是带引号的字符串
          if (path === 'token' && parsed !== null) {
            state[path] = String(parsed).replace(/^"|"$/g, '')
          } else {
            state[path] = parsed
          }
        } catch {
          // 如果解析失败，直接使用字符串值
          state[path] = stored.replace(/^"|"$/g, '')
        }
      }
    } catch (error) {
      console.error(`Failed to restore ${path} from localStorage:`, error)
      // 保持默认值
    }
  })
}

/**
 * 将状态持久化到 localStorage
 * @param {Object} state - Vuex state 对象
 * @param {Array<string>} paths - 需要持久化的状态路径
 */
function persistState(state, paths) {
  paths.forEach(path => {
    const key = STORAGE_KEY_PREFIX + path
    try {
      let value = state[path]
      
      // 特殊处理 user 对象，避免存储过大的头像数据
      if (path === 'user' && value && typeof value === 'object') {
        value = { ...value }
        // 如果头像数据过大（超过50KB），不存储到 localStorage
        if (value.avatar && value.avatar.length > 50000) {
          // 保存头像到单独的 key
          localStorage.setItem(STORAGE_KEY_PREFIX + 'user_avatar', value.avatar)
          // 在 user 对象中只保留一个标记
          value.avatar = '__stored_in_local__'
        }
      }
      
      if (value === null || value === undefined) {
        localStorage.removeItem(key)
      } else {
        // 如果是对象或数组，序列化为 JSON；否则直接存储
        const serialized = typeof value === 'object'
          ? JSON.stringify(value)
          : String(value)
        localStorage.setItem(key, serialized)
      }
    } catch (error) {
      console.error(`Failed to persist ${path} to localStorage:`, error)
    }
  })
}

/**
 * 创建 Vuex 持久化插件
 * @param {Object} options - 插件配置
 * @param {Array<string>} options.paths - 需要持久化的状态路径数组
 * @param {string} [options.keyPrefix='app_'] - localStorage 键前缀
 * @returns {Function} Vuex 插件函数
 */
export function createPersistPlugin(options = {}) {
  const { paths = [], keyPrefix = 'app_' } = options

  return store => {
    // 初始化时从 localStorage 恢复状态
    restoreState(store.state, paths)
    
    // 恢复用户头像（如果存在）
    try {
      const storedAvatar = localStorage.getItem(keyPrefix + 'user_avatar')
      if (storedAvatar && store.state.user && store.state.user.avatar === '__stored_in_local__') {
        store.state.user.avatar = storedAvatar
      }
    } catch (error) {
      console.error('Failed to restore user avatar:', error)
    }

    // 订阅 mutation，在每次状态变更后持久化
    store.subscribe((mutation, state) => {
      persistState(state, paths)
    })
  }
}
